import axios from 'axios';
import { ScavengerItem, Submission, Stats } from '../types';

// In production, use relative URLs. In development, use localhost.
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  login: async (teamId: string, passcode: string) => {
    const response = await api.post('/auth/login', { teamId, passcode });
    const { token, group } = response.data;
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('group', JSON.stringify(group));
    return { token, group };
  },

  logout: () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('group');
    sessionStorage.removeItem('huntmaster_redirect_session');
  },

  getTeams: async () => {
    const response = await api.get('/auth/teams');
    return response.data;
  },

  updateTeamName: async (teamId: number, name: string) => {
    const response = await api.put('/auth/update-team-name', { teamId, name });
    return response.data;
  },

  uploadTeamPicture: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('teamPicture', imageFile);
    
    try {
      const response = await api.post('/scavenger/team-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfter || 5;
        throw new Error(`Too many uploads in progress. Please wait ${retryAfter} seconds and try again.`);
      }
      throw error;
    }
  },

  deleteTeamPicture: async () => {
    const response = await api.delete('/scavenger/team-picture');
    return response.data;
  },

  getStoredAuth: () => {
    const token = sessionStorage.getItem('token');
    const groupStr = sessionStorage.getItem('group');
    const group = groupStr ? JSON.parse(groupStr) : null;
    return { token, group };
  }
};

export const scavengerService = {
  getItems: async (): Promise<ScavengerItem[]> => {
    const response = await api.get('/scavenger/items');
    return response.data;
  },

  getSubmissions: async (): Promise<Submission[]> => {
    const response = await api.get('/scavenger/submissions');
    // Map backend response to frontend interface
    return response.data.map((submission: any) => ({
      id: submission.id,
      itemId: submission.itemId,
      imagePath: submission.imagePath,
      timestamp: submission.submittedAt,
      status: submission.status || 'unknown',
      reviewedAt: submission.reviewedAt,
      reviewedBy: submission.reviewedBy
    }));
  },

  submitImage: async (itemId: number, imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    try {
      const response = await api.post(`/scavenger/submit/${itemId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      // Handle rate limiting
      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfter || 5;
        throw new Error(`Too many uploads in progress. Please wait ${retryAfter} seconds and try again.`);
      }
      throw error;
    }
  },

  deleteImage: async (itemId: number) => {
    const response = await api.delete(`/scavenger/submit/${itemId}`);
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get('/scavenger/stats');
    return response.data;
  },

  getSubmissionStatus: async () => {
    const response = await api.get('/scavenger/submission-status');
    return response.data;
  },

  getScoreReleaseStatus: async () => {
    const response = await api.get('/scavenger/score-release-status');
    return response.data;
  }
};

export const huntmasterService = {
  getAllSubmissions: async (): Promise<Submission[]> => {
    const response = await api.get('/scavenger/huntmaster/submissions');
    return response.data.map((submission: any) => ({
      id: submission.id,
      itemId: submission.itemId,
      imagePath: submission.imagePath,
      timestamp: submission.submittedAt,
      status: submission.status || 'unknown',
      reviewedAt: submission.reviewedAt,
      reviewedBy: submission.reviewedBy,
      groupId: submission.groupId
    }));
  },

  updateSubmissionStatus: async (submissionId: number, status: 'correct' | 'wrong' | 'unknown') => {
    const response = await api.patch(`/scavenger/huntmaster/submissions/${submissionId}/status`, { status });
    return response.data;
  },

  getAllTeams: async () => {
    const response = await api.get('/scavenger/huntmaster/teams');
    return response.data;
  },

  getSubmissionStatus: async () => {
    const response = await api.get('/scavenger/huntmaster/submission-status');
    return response.data;
  },

  toggleSubmissions: async (enabled: boolean) => {
    const response = await api.post('/scavenger/huntmaster/toggle-submissions', { enabled });
    return response.data;
  },

  getScoreReleaseStatus: async () => {
    const response = await api.get('/scavenger/score-release-status');
    return response.data;
  },

  toggleScoreRelease: async (released: boolean) => {
    const response = await api.post('/scavenger/huntmaster/toggle-score-release', { released });
    return response.data;
  }
};

export const imageService = {
  getImageUrl: (filename: string): string => {
    // Use the API base URL for uploads endpoint
    const baseUrl = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
    return `${baseUrl}/uploads/${filename}`;
  }
};

export default api;
